import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { toast } from 'sonner';

const CartPage = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    const saved = localStorage.getItem('indicraft_cart');
    if (saved) {
      setCartItems(JSON.parse(saved));
    }
  };

  const updateQuantity = (productId, delta) => {
    const newCart = cartItems.map(item => {
      if (item.id === productId) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQuantity };
      }
      return item;
    });
    setCartItems(newCart);
    localStorage.setItem('indicraft_cart', JSON.stringify(newCart));
  };

  const removeItem = (productId) => {
    const newCart = cartItems.filter(item => item.id !== productId);
    setCartItems(newCart);
    localStorage.setItem('indicraft_cart', JSON.stringify(newCart));
    toast.success('Item removed from cart');
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('indicraft_cart');
    toast.success('Cart cleared');
  };

  const subtotal = cartItems.reduce((sum, item) => sum + (item.discount_price * item.quantity), 0);
  const shipping = subtotal >= 499 ? 0 : 50;
  const total = subtotal + shipping;

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)} />
      
      <main className="flex-grow py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-maroon hover:text-maroon-dark mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Continue Shopping
          </button>

          <h1 className="text-3xl md:text-4xl font-heading font-bold text-maroon mb-8">
            Shopping Cart
          </h1>

          {cartItems.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6">
                <ShoppingBag className="w-12 h-12 text-gold/50" />
              </div>
              <h2 className="text-2xl font-heading font-semibold text-maroon mb-3">
                Your Cart is Empty
              </h2>
              <p className="text-maroon/60 mb-6 max-w-md mx-auto">
                Looks like you haven't added any divine products to your cart yet. 
                Explore our collection of sacred puja essentials.
              </p>
              <Button
                onClick={() => navigate('/shop')}
                className="bg-maroon hover:bg-maroon-dark text-white font-semibold px-8 py-6 rounded-full"
              >
                <ShoppingBag className="w-5 h-5 mr-2" />
                Start Shopping
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl border border-gold/10 overflow-hidden">
                  <div className="p-4 bg-cream border-b border-gold/10 flex items-center justify-between">
                    <span className="font-medium text-maroon">
                      {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} in cart
                    </span>
                    <Button
                      variant="ghost"
                      onClick={clearCart}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Clear Cart
                    </Button>
                  </div>

                  <div className="divide-y divide-gold/10">
                    {cartItems.map((item) => (
                      <div 
                        key={item.id} 
                        className="p-4 md:p-6 flex gap-4"
                        data-testid={`cart-item-${item.id}`}
                      >
                        <div 
                          className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden bg-cream flex-shrink-0 cursor-pointer"
                          onClick={() => navigate(`/product/${item.id}`)}
                        >
                          <img
                            src={item.images?.[0] || 'https://via.placeholder.com/200'}
                            alt={item.name}
                            className="w-full h-full object-cover hover:scale-105 transition-transform"
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 
                            className="font-heading font-semibold text-maroon mb-1 cursor-pointer hover:text-maroon-dark"
                            onClick={() => navigate(`/product/${item.id}`)}
                          >
                            {item.name}
                          </h3>
                          
                          {item.discount_percentage > 0 && (
                            <span className="inline-block bg-saffron/10 text-saffron text-xs font-medium px-2 py-1 rounded-full mb-2">
                              {item.discount_percentage}% OFF
                            </span>
                          )}

                          <div className="flex items-baseline gap-2 mb-3">
                            <span className="text-xl font-bold text-maroon">
                              ₹{item.discount_price}
                            </span>
                            {item.original_price > item.discount_price && (
                              <span className="text-sm text-maroon/50 line-through">
                                ₹{item.original_price}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 bg-cream rounded-full p-1">
                              <button
                                onClick={() => updateQuantity(item.id, -1)}
                                className="w-8 h-8 rounded-full bg-white hover:bg-gold/10 flex items-center justify-center transition-colors"
                                data-testid={`decrease-qty-${item.id}`}
                              >
                                <Minus className="w-4 h-4 text-maroon" />
                              </button>
                              <span className="w-8 text-center font-semibold text-maroon">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.id, 1)}
                                className="w-8 h-8 rounded-full bg-white hover:bg-gold/10 flex items-center justify-center transition-colors"
                                data-testid={`increase-qty-${item.id}`}
                              >
                                <Plus className="w-4 h-4 text-maroon" />
                              </button>
                            </div>

                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                              data-testid={`remove-item-${item.id}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>

                          <p className="text-sm text-maroon/60 mt-2">
                            Subtotal: <span className="font-semibold text-maroon">₹{(item.discount_price * item.quantity).toFixed(2)}</span>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl p-6 border border-gold/10 sticky top-24">
                  <h2 className="font-heading font-semibold text-maroon text-xl mb-6">
                    Order Summary
                  </h2>

                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-maroon/70">
                      <span>Subtotal ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                      <span>₹{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-maroon/70">
                      <span>Shipping</span>
                      <span className={shipping === 0 ? 'text-green-600 font-medium' : ''}>
                        {shipping === 0 ? 'FREE' : `₹${shipping}`}
                      </span>
                    </div>
                    
                    {subtotal < 499 && subtotal > 0 && (
                      <div className="p-3 bg-saffron/10 rounded-xl">
                        <div className="flex items-center gap-2 text-saffron">
                          <Tag className="w-4 h-4" />
                          <span className="text-sm font-medium">
                            Add ₹{(499 - subtotal).toFixed(0)} more for FREE shipping!
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-gold/10 mb-6">
                    <div className="flex justify-between font-bold text-xl text-maroon">
                      <span>Total</span>
                      <span>₹{total.toFixed(2)}</span>
                    </div>
                    <p className="text-sm text-maroon/50 mt-1">
                      (Inclusive of all taxes)
                    </p>
                  </div>

                  <Button
                    onClick={() => navigate('/checkout')}
                    className="w-full bg-maroon hover:bg-maroon-dark text-white font-semibold py-6 rounded-full text-lg"
                    data-testid="proceed-to-checkout"
                  >
                    Proceed to Checkout
                  </Button>

                  <div className="mt-6 p-4 bg-cream rounded-xl">
                    <div className="flex items-center gap-2 text-maroon/70 text-sm">
                      <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span>Secure Checkout with SSL Encryption</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CartPage;
